import React, { useEffect, useMemo, useState } from 'react'
import Header from './Header'
import SubHeader from './SubHeader'
import DataTable, { formatNum } from "./DataTable";
import TableLoader from "../TableLoader";
import Apiauth from "../../../API/Apiauth";
import StockAgingChart from './graph/StockAgingChart';
import { toFormatPrice } from "../helpers/function";


function toLocationRows(row = {}) {
  const candidate =
    row.locations ||
    [];

  if (!Array.isArray(candidate)) return [];

  return candidate.map((loc, idx) => ({
    _id: `${loc.location_id || loc.location_name || loc.location || "loc"}-${idx}`,
    locationName:
      loc.location_name || loc.location_Name || loc.location || "Location",
    qty: loc.qty,
    salesAmount: loc.sales_amount,
    cost: loc.cost,
    profit: loc.profit_pct,
  }));
}

function mapRows(apiData = []) {
  return apiData.map((row, idx) => ({
    _rowKey: String(
      row.age || row.aging || row.age_group || row.aging_group || `aging-${idx}`
    ),
    age: row.age || row.aging || row.age_group || row.aging_group || "-",
    qty: row.qty,
    salesAmount: row.sales_amount,
    cost: row.cost,
    profit: row.profit_pct,
    locations: toLocationRows(row),
  }));
}

function getAgeBandOrder(ageValue) {
  const s = String(ageValue ?? "").trim().toLowerCase();
  if (!s) return Number.MAX_SAFE_INTEGER;

  // Put open-ended bands like ">5" at the end of normal ranges.
  if (s.startsWith(">")) {
    const n = Number(s.replace(/[^\d.]/g, ""));
    return Number.isFinite(n) ? n + 1000 : Number.MAX_SAFE_INTEGER;
  }

  // Handles "0-1 year", "1 - 2", "2-3 years"
  const rangeStart = s.match(/^(\d+(?:\.\d+)?)/);
  if (rangeStart) return Number(rangeStart[1]);

  // Fallback for any string containing a number.
  const anyNum = s.match(/(\d+(?:\.\d+)?)/);
  if (anyNum) return Number(anyNum[1]);

  return Number.MAX_SAFE_INTEGER;
}

function sortRowsByAgeBand(rows = []) {
  return [...rows].sort((a, b) => {
    const oa = getAgeBandOrder(a.age);
    const ob = getAgeBandOrder(b.age);
    if (oa !== ob) return oa - ob;
    return String(a.age ?? "").localeCompare(String(b.age ?? ""));
  });
}

function mapTotals(apiTotals = {}) {
  return {
    qty: apiTotals.qty,
    salesAmount: apiTotals.sales_amount,
    cost: apiTotals.cost,
    profit: apiTotals.profit_pct,
  };
}

function mapMetrics(summary = {}) {
  return [
    { label: "Sales Amount", value: toFormatPrice(Number(summary.sales_amount || 0)) },
    { label: "Profit", value: toFormatPrice(Number(summary.profit || 0)) },
    { label: "Average % Profit", value: formatNum(summary.average_profit_pct) },
    { label: "Top Aging Group by Sales", value: summary.top_aging_group_by_sales },
  ];
}

const COLUMNS = [
  { key: "_index", label: "#", align: "center", width: 40 },
  { key: "age", label: "Age", align: "left", width: 230 },
  { key: "qty", label: "Qty", align: "left", width: 150 },
  {
    key: "salesAmount",
    label: "Sales Amount",
    align: "left",
    width: 180,
    isPriceColumn: true,
  },
  { key: "cost", label: "Cost", align: "right", width: 120, isPriceColumn: true },
  { key: "profit", label: "Profit %", align: "right", width: 120 },
];

const DEFAULT_PAGE_SIZE = 10;
const CHART_MAX_LIMIT = 100000;

const StockAging = () => {
const [rows, setRows] = useState([]);
     const [chartRows, setChartRows] = useState([]);
     const [totals, setTotals] = useState(null);
     const [metrics, setMetrics] = useState([]);
     const [filter, setFilter] = useState({});
     const [loading, setLoading] = useState(true);
     const [appliedFilters, setAppliedFilters] = useState({});
     const [totalRecords, setTotalRecords] = useState(0);
     const [page, setPage] = useState(1);
     const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
     const [showCharts, setShowCharts] = useState(false); 
     const [expandedRows, setExpandedRows] = useState({});

     
  const fetchStockAgingData = ({page: p = 1,
    limit: l = pageSize,
    ...rest}={}) => {
 
     setLoading(true);
    Apiauth({ page: p, limit: l, ...rest }, "Inventory/reports/salesByStockAging")
      .then((res) => {
        if (res?.code === 200) {
          setRows(sortRowsByAgeBand(mapRows(res.data)));
          setTotals(mapTotals(res.totals));
          setMetrics(mapMetrics(res.summary));
         setFilter(res.filters ?? {});
         setTotalRecords(res?.pagination?.total ?? 0);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

    const fetchChartReport = (filters = {}) => {
    setLoading(true);
    Apiauth(
      { page: 1, limit: CHART_MAX_LIMIT, ...filters },
      "Inventory/reports/salesByStockAging"
    )
      .then((res) => {
        if (res?.code === 200) {
          setChartRows(sortRowsByAgeBand(mapRows(res.data)));
        }
      })
      .finally(() => setLoading(false));
  };
    useEffect(() => {
    fetchStockAgingData({ page: 1, limit: DEFAULT_PAGE_SIZE });
  }, []);
useEffect(() => {
    if (showCharts) {
      fetchChartReport(appliedFilters);
    }
  }, [showCharts, appliedFilters]);

  function handleFetchData({ page: newPage, limit: newLimit }) {
    if (newLimit !== pageSize) setPageSize(newLimit);
    setPage(newPage);
    fetchStockAgingData({ ...appliedFilters, page: newPage, limit: newLimit });
  }

  function handleFilterApply(filterParams = {}) {
    setAppliedFilters(filterParams);
    setPage(1);
    setExpandedRows({});
    fetchStockAgingData({ ...filterParams, page: 1, limit: pageSize });
  }

  function toggleExpanded(rowKey) {
    setExpandedRows((prev) => ({
      ...prev,
      [rowKey]: !prev[rowKey],
    }));
  }

  function renderMetricValue(key, value) {
    if (value == null || value === "") return "";
    if (key === "profit") {
      const n = Number(value);
      if (!Number.isFinite(n)) return value;
      const color = n >= 100 ? "#2db7a3" : "#de5a58";
      return <span style={{ color }}>{`${n}%`}</span>;
    }
    if (key === "salesAmount" || key === "cost") {
      return toFormatPrice(Number(value || 0));
    }
    return formatNum(value);
  }

  const displayRows = useMemo(() => {
    const flat = [];
    rows.forEach((row, idx) => {
      flat.push({
        ...row,
        _isChild: false,
        _parentIndex: idx + 1,
        _stripeIndex: idx,
      });
      if (expandedRows[row._rowKey] && Array.isArray(row.locations)) {
        row.locations.forEach((loc) => {
          flat.push({
            _rowKey: `${row._rowKey}-${loc._id}`,
            _parentKey: row._rowKey,
            _isChild: true,
            _parentIndex: idx + 1,
            _stripeIndex: idx,
            age: loc.locationName,
            qty: loc.qty,
            salesAmount: loc.salesAmount,
            cost: loc.cost,
            profit: loc.profit,
          });
        });
      }
    });
    return flat;
  }, [rows, expandedRows]);

  function renderCell(col, row) {
    const hasLocations = Array.isArray(row.locations) && row.locations.length > 0;
    const isExpanded = !!expandedRows[row._rowKey];

    if (row._isChild) {
      if (col.key === "_index") return "";
      if (col.key === "age") {
        return (
          <span style={{ paddingLeft: 16, color: "#606060", fontSize: 14, fontFamily: "Segoe UI" }}>
            {row.age}
          </span>
        );
      }
      return (
        <span style={{ color: "#606060", fontSize: 14, fontFamily: "Segoe UI" }}>
          {renderMetricValue(col.key, row[col.key])}
        </span>
      );
    }

    if (col.key === "_index") {
      return row._parentIndex || row._rowIndex;
    }

    if (col.key === "age") {
      return (
        <div
          onClick={() => hasLocations && toggleExpanded(row._rowKey)}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            cursor: hasLocations ? "pointer" : "default",
          }}
          title={hasLocations ? "Expand locations" : ""}
        >
          <span>{row.age}</span>
          {hasLocations && (
            <span style={{ fontSize: 12, color: "#8b8b8b" }}>
              {isExpanded ? "▲" : "▼"}
            </span>
          )}
        </div>
      );
    }

    return renderMetricValue(col.key, row[col.key]);
  }


  return (
    <>
   <Header title="Sales by Stock Aging" metrics={metrics}/>
   <SubHeader filters={filter} showCharts={showCharts}  setShowCharts={setShowCharts} onFilterApply={handleFilterApply}
   />
    {loading ? (
           <TableLoader />
         ) : (
             showCharts?(<StockAgingChart rows={chartRows}/>):
          ( <DataTable
             columns={COLUMNS}
             data={displayRows}
             totals={totals}
             renderCell={renderCell}
              showSearch={true}
             showDateRange={true}
             pageLimit={pageSize}
             totalRecords={totalRecords}
             currentPage={page}
             onPageChange={setPage}
             fetchData={handleFetchData}
           />)
         )}
   </>
  )
}

export default StockAging